/* eslint-disable no-unused-vars */
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import "./VideoHeroCarousel.css";
import { useSelector } from "react-redux";
import * as posterImage from "../../utils/usePosterImage";
import parse from "html-react-parser";
import { Link } from "react-router-dom";
import { getTargetFromType, useGetLink } from "../../utils/useOpenLink";
import useCustomTranslation from "../../utils/useCustomTranslation";
import { Button } from "../../components/Button";
import { APP_COLORS } from "../../constants/styles";

export default function VideoHeroCarousel({ contents: contentsArray, colors: colorObj }) {
    const content = useSelector((state) => state.layout.content);
    const { getLink } = useGetLink();
    const { t } = useCustomTranslation();
    const swiperRef = useRef(null);

    const [activeIndex, setActiveIndex] = useState(0);
    const [playingIndex, setPlayingIndex] = useState(null);
    const [isMuted, setIsMuted] = useState(true);
    const [videoStarted, setVideoStarted] = useState(false);
    const [contentReferenceData, setContentReferenceData] = useState([]);
    const [posterVisible, setPosterVisible] = useState([]);

    useEffect(() => {
        setPlayingIndex(activeIndex);
        setVideoStarted(false);

        setPosterVisible((prev) => {
            const updated = [...prev];
            updated[activeIndex] = "visible";
            return updated;
        });
    }, [activeIndex]);

    useEffect(() => {
        const fetchDefaultData = async () => {
            const allProcessedData = [];

            for (const iterator of contentsArray) {
                const respKeys = content[iterator.contentKey];
                let resp = (respKeys || []).map((k) => content[k]);
                resp = JSON.parse(JSON.stringify(resp));

                const posterPromises = resp.map((element) => {
                    if (element?.link) {
                        return posterImage
                            .usePosterImage(element.link)
                            .then((posterResult) => ({ ...element, posterImage: posterResult }))
                            .catch(() => element);
                    }
                    return Promise.resolve(element);
                });

                const responsesArray = await Promise.allSettled(posterPromises);

                const finalResp = [];
                responsesArray.forEach((res) => {
                    if (res.status === "fulfilled") {
                        finalResp.push(res.value);
                    }
                });

                allProcessedData.push(...finalResp);
            }

            setContentReferenceData(allProcessedData);
        };

        fetchDefaultData();
    }, [content]);

    useEffect(() => {
        if (contentReferenceData.length > 0) {
            setPosterVisible(Array(contentReferenceData.length).fill(false));
        }
    }, [contentReferenceData]);

    return (
        <div className="hero-section" style={APP_COLORS(colorObj)}>
            <Swiper
                modules={[Autoplay, Pagination, Navigation]}
                pagination={{ clickable: true }}
                navigation={true}
                className="video-swiper"
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                onSlideChange={(swiper) => {
                    setActiveIndex(swiper.realIndex);
                }}
                style={{ "--primary_color": colorObj?.primary_color }}
            >
                {contentReferenceData.map((video, idx) => (
                    <SwiperSlide key={idx}>
                        <div className="video-slide">
                            {video?.link ? (
                                <>
                                    <ReactPlayer
                                        key={`player-${video.link.slice(0, 20)}-${playingIndex}`}
                                        url={video.link}
                                        playing={playingIndex === idx}
                                        muted={isMuted}
                                        onPlay={() => {
                                            setVideoStarted(true);
                                            setPosterVisible((prev) => {
                                                const updated = [...prev];
                                                updated[idx] = "fading";
                                                return updated;
                                            });
                                        }}
                                        width="100%"
                                        height="100%"
                                        className="react-player"
                                        onEnded={() => {
                                            if (swiperRef.current) {
                                                const nextIndex = swiperRef.current.realIndex + 1;
                                                if (nextIndex >= contentReferenceData.length) {
                                                    swiperRef.current.slideTo(0);
                                                } else {
                                                    swiperRef.current.slideNext();
                                                }
                                            }
                                        }}
                                    />
                                    {posterVisible[idx] !== "hidden" && (
                                        <img
                                            src={video?.image?.src}
                                            className={`video-thumbnail ${
                                                posterVisible[idx] === "fading" ? "fade-out" : ""
                                            }`}
                                            alt="thumbnail"
                                        />
                                    )}
                                </>
                            ) : (
                                <img
                                    src={video?.image?.src}
                                    className="video-thumbnail"
                                    alt="thumbnail"
                                />
                            )}

                            <div className="video-shadow-overlay"></div>

                            <div
                                className={`hero-video-overlay ${
                                    video?.link ? (videoStarted ? "down" : "") : "down"
                                }`}
                            >
                                <div className="genres fade-item delay-1">
                                    <a className="genre-link text-primary_color">
                                        {video.theme && parse(video.theme)}
                                    </a>
                                </div>
                                <Link
                                    to={getLink(
                                        video?.path,
                                        video?.type,
                                        video?.language,
                                        video?.link,
                                    )}
                                    target={getTargetFromType(video?.type, video?.link)}
                                >
                                    <h2 className="video-title hover:underline fade-item delay-2">
                                        {video.title}
                                    </h2>
                                </Link>
                                <p className="description fade-item delay-4">
                                    {parse(video?.introduction || "")}
                                </p>
                                <Link
                                    to={getLink(
                                        video?.path,
                                        video?.type,
                                        video?.language,
                                        video?.link,
                                    )}
                                    target={getTargetFromType(video?.type, video?.link)}
                                >
                                    <div className="buttons fade-item delay-5">
                                        <button className="btn-play bg-primary_color">
                                            {t("btnText.learnMore")}
                                        </button>
                                    </div>
                                </Link>
                            </div>

                            <div className="progress-wrapper">
                                <span className="slide-number">{activeIndex + 1}</span>
                                <div className="single-progress-bar">
                                    <div
                                        className="single-progress-fill bg-primary_color"
                                        style={{
                                            width: `${
                                                ((activeIndex + 1) / contentReferenceData.length) *
                                                100
                                            }%`,
                                        }}
                                    />
                                </div>
                                <span className="slide-number">{contentReferenceData.length}</span>
                                {video?.link && (
                                    <Button
                                        className="mute-toggle-btn cursor-pointer bg-primary_color"
                                        onClick={() => setIsMuted((prev) => !prev)}
                                    >
                                        <i
                                            className={
                                                isMuted ? "pi pi-volume-off" : "pi pi-volume-up"
                                            }
                                        />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
}
